import { DEAL_STATUS_CONFIG, DEAL_STATUSES } from '../constants';

/**
 * Get the color scheme for a trade status
 * @param status - The trade status string
 * @returns The color scheme for the status badge
 */
export function getStatusColor(status: string): string {
  const norm = status.toLowerCase();
  if (norm === 'complete' || norm === 'completed') return 'green';
  const statusKey = norm as keyof typeof DEAL_STATUS_CONFIG;
  return DEAL_STATUS_CONFIG[statusKey]?.color || 'gray';
}

/**
 * Get custom styling for status badges that need special colors
 * @param status - The trade status string
 * @returns Custom styling object or null
 */
export function getStatusBadgeProps(status: string): any {
  const statusKey = status.toLowerCase();
  
  if (statusKey === 'rejected') {
    return {
      colorScheme: 'red',
      bg: 'red.800',
      color: 'white',
      _hover: { bg: 'red.700' }
    };
  }
  
  return null;
}

/**
 * Get the display text for a trade status
 * @param status - The trade status string
 * @returns The human-readable text for the status
 */
export function getStatusText(status: string): string {
  const norm = status.toLowerCase();
  if (norm === 'complete' || norm === 'completed') return 'Completed';
  // Show "Canceled" for rejected status (to be nicer to users)
  if (norm === 'rejected') return 'Canceled';
  const statusKey = norm as keyof typeof DEAL_STATUS_CONFIG;
  return DEAL_STATUS_CONFIG[statusKey]?.text || status;
}

/**
 * Check if a trade status indicates the trade is completed
 * @param status - The trade status string
 * @returns True if the trade is in a completed state
 */
export const TERMINATED_STATUSES = [
  DEAL_STATUSES.APPROVED,
  DEAL_STATUSES.DECLINED,
  DEAL_STATUSES.EXPIRED,
  DEAL_STATUSES.REJECTED,
  DEAL_STATUSES.WITHDRAWN,
  DEAL_STATUSES.UNDER_REVIEW,
  DEAL_STATUSES.LEAGUE_REVIEW,
  DEAL_STATUSES.HAS_SALARY_ISSUE,
  'completed',
  'complete',
];

/**
 * Check if a trade status indicates the trade is completed
 * @param status - The trade status string
 * @returns True if the trade is in a completed state
 */
export function isTradeCompleted(status: string): boolean {
  const statusLower = status.toLowerCase();
  return TERMINATED_STATUSES.some(terminatedStatus => terminatedStatus === statusLower);
} 